<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <header class="bg-white shadow-sm border-b">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center py-4">
          <div>
            <h1 class="text-2xl font-bold text-gray-900">Lead Management</h1>
            <p class="text-gray-600">Manage and export your leads</p>
          </div>
          <div class="flex space-x-3">
            <button @click="exportLeads" :disabled="loading" class="btn-secondary-sm">
              <svg v-if="!loading" class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              <div v-else class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Export CSV
            </button>
            <button @click="goBack" class="btn-tertiary">
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Filters and Search -->
      <div class="bg-white rounded-lg shadow mb-6">
        <div class="p-6 border-b border-gray-200">
          <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
            <!-- Search -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <input
                v-model="filters.search"
                @input="debouncedSearch"
                type="text"
                placeholder="Search by name, email, or company..."
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <!-- Show Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Show</label>
              <select
                v-model="filters.showId"
                @change="loadLeads"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Shows</option>
                <option v-for="show in shows" :key="show.id" :value="show.id">
                  {{ show.name }}
                </option>
              </select>
            </div>

            <!-- Follow-up Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Follow-up Status</label>
              <select
                v-model="filters.followUpStatus"
                @change="loadLeads"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Leads</option>
                <option value="true">Followed Up</option>
                <option value="false">Not Followed Up</option>
              </select>
            </div>

            <!-- Date Range -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
              <select
                v-model="filters.dateRange"
                @change="loadLeads"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Time</option>
                <option value="7">Last 7 Days</option>
                <option value="30">Last 30 Days</option>
                <option value="90">Last 90 Days</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Bulk Actions (visible when selection is made) -->
        <div v-if="selectedLeads.length > 0" class="px-6 py-4 bg-gray-50 border-b border-gray-200 sticky top-[56px] z-10">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-4">
              <label class="flex items-center">
                <input
                  v-model="selectAll"
                  @change="toggleSelectAll"
                  type="checkbox"
                  class="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span class="ml-2 text-sm text-gray-700">Select All</span>
              </label>
              <span class="text-sm text-gray-600">{{ selectedLeads.length }} selected</span>
            </div>
            <div class="flex space-x-2">
              <button @click="bulkUpdateFollowUp(true)" class="btn-secondary text-sm px-3 py-1">Mark Followed Up</button>
              <button @click="bulkUpdateFollowUp(false)" class="btn-secondary text-sm px-3 py-1">Mark Not Followed Up</button>
              <button @click="exportSelected" class="btn-primary text-sm px-3 py-1">Export Selected</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Leads Table -->
      <div class="bg-white rounded-lg shadow">
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    v-model="selectAll"
                    @change="toggleSelectAll"
                    type="checkbox"
                    class="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lead
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Show
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Interest
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Follow-up
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-if="loading" class="text-center">
                <td colspan="7" class="px-6 py-12">
                  <div class="flex items-center justify-center">
                    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    <span class="ml-3 text-gray-600">Loading leads...</span>
                  </div>
                </td>
              </tr>
              <tr v-else-if="leads.length === 0" class="text-center">
                <td colspan="7" class="px-6 py-12 text-gray-500">
                  No leads found
                </td>
              </tr>
              <tr v-else v-for="lead in leads" :key="lead.id" class="hover:bg-gray-50 cursor-pointer" @click="viewLeadDetail(lead)">
                <td class="px-6 py-4 whitespace-nowrap">
                  <input
                    v-model="selectedLeads"
                    :value="lead.id"
                    type="checkbox"
                    class="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div class="text-sm font-medium text-gray-900">{{ lead.name }}</div>
                    <div class="text-sm text-gray-500">{{ lead.email }}</div>
                    <div v-if="lead.company" class="text-sm text-gray-500">{{ lead.company }}</div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-900">{{ lead.show_name }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span v-if="lead.interest" class="inline-flex px-2.5 py-1 text-[12px] font-medium rounded-full bg-sky-50 text-sky-700">
                    {{ lead.interest }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    @click.stop="toggleFollowUp(lead)"
                    :class="[
                      'inline-flex px-2.5 py-1 text-[12px] font-medium rounded-full',
                      lead.is_followed_up
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-gray-100 text-gray-700'
                    ]"
                  >
                    {{ lead.is_followed_up ? 'Followed Up' : 'Not Followed Up' }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ formatDateTime(lead.created_at) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                  <span class="text-gray-400">→</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="pagination.pages > 1" class="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
          <div class="flex items-center justify-between">
            <div class="flex-1 flex justify-between sm:hidden">
              <button
                @click="changePage(pagination.page - 1)"
                :disabled="!pagination.has_prev"
                class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                @click="changePage(pagination.page + 1)"
                :disabled="!pagination.has_next"
                class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p class="text-sm text-gray-700">
                  Showing
                  <span class="font-medium">{{ (pagination.page - 1) * pagination.per_page + 1 }}</span>
                  to
                  <span class="font-medium">{{ Math.min(pagination.page * pagination.per_page, pagination.total) }}</span>
                  of
                  <span class="font-medium">{{ pagination.total }}</span>
                  results
                </p>
              </div>
              <div>
                <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    @click="changePage(pagination.page - 1)"
                    :disabled="!pagination.has_prev"
                    class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    v-for="page in visiblePages"
                    :key="page"
                    @click="changePage(page)"
                    :class="[
                      'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                      page === pagination.page
                        ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    ]"
                  >
                    {{ page }}
                  </button>
                  <button
                    @click="changePage(pagination.page + 1)"
                    :disabled="!pagination.has_next"
                    class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Lead Detail Modal -->
    <LeadDetail
      v-if="showLeadDetail"
      :lead="selectedLead"
      @close="showLeadDetail = false"
      @update="loadLeads"
    />
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import config from '../config.js'
import LeadDetail from '../components/LeadDetail.vue'

// Router
const router = useRouter()
const route = useRoute()

// Reactive data
const loading = ref(false)
const leads = ref([])
const shows = ref([])
const selectedLeads = ref([])
const selectAll = ref(false)
const showLeadDetail = ref(false)
const selectedLead = ref(null)

const filters = reactive({
  search: '',
  showId: '',
  followUpStatus: '',
  dateRange: ''
})

const pagination = reactive({
  page: 1,
  per_page: 20,
  total: 0,
  pages: 0,
  has_next: false,
  has_prev: false
})

// Debounced search
let searchTimeout = null
const debouncedSearch = () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    pagination.page = 1
    loadLeads()
  }, 300)
}

// Computed properties
const visiblePages = computed(() => {
  const pages = []
  const current = pagination.page
  const total = pagination.pages
  
  if (total <= 7) {
    for (let i = 1; i <= total; i++) {
      pages.push(i)
    }
  } else {
    if (current <= 4) {
      for (let i = 1; i <= 5; i++) {
        pages.push(i)
      }
      pages.push('...')
      pages.push(total)
    } else if (current >= total - 3) {
      pages.push(1)
      pages.push('...')
      for (let i = total - 4; i <= total; i++) {
        pages.push(i)
      }
    } else {
      pages.push(1)
      pages.push('...')
      for (let i = current - 1; i <= current + 1; i++) {
        pages.push(i)
      }
      pages.push('...')
      pages.push(total)
    }
  }
  
  return pages
})

// Methods
const loadShows = async () => {
  try {
    const token = localStorage.getItem('admin_token')
    if (!token) {
      router.push('/admin/login')
      return
    }

    const response = await fetch(`${config.apiBaseUrl}/admin/dashboard`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (response.ok) {
      const data = await response.json()
      shows.value = data.shows
    }
  } catch (error) {
    console.error('Error loading shows:', error)
  }
}

const loadLeads = async () => {
  try {
    loading.value = true
    const token = localStorage.getItem('admin_token')
    if (!token) {
      router.push('/admin/login')
      return
    }

    // Build query parameters
    const params = new URLSearchParams({
      page: pagination.page.toString(),
      per_page: pagination.per_page.toString()
    })

    if (filters.showId) {
      params.append('show_id', filters.showId)
    }

    if (filters.search) {
      params.append('search', filters.search)
    }

    if (filters.followUpStatus !== '') {
      params.append('follow_up_status', filters.followUpStatus)
    }

    if (filters.dateRange) {
      const days = parseInt(filters.dateRange)
      const date = new Date()
      date.setDate(date.getDate() - days)
      params.append('date_from', date.toISOString().split('T')[0])
    }

    const response = await fetch(`${config.apiBaseUrl}/admin/leads?${params}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('admin_token')
        router.push('/admin/login')
        return
      }
      throw new Error('Failed to load leads')
    }

    const data = await response.json()
    leads.value = data.leads
    Object.assign(pagination, data.pagination)
    
  } catch (error) {
    console.error('Error loading leads:', error)
  } finally {
    loading.value = false
  }
}

const changePage = (page) => {
  if (page >= 1 && page <= pagination.pages) {
    pagination.page = page
    loadLeads()
  }
}

const toggleSelectAll = () => {
  if (selectAll.value) {
    selectedLeads.value = leads.value.map(lead => lead.id)
  } else {
    selectedLeads.value = []
  }
}

const toggleFollowUp = async (lead) => {
  try {
    const token = localStorage.getItem('admin_token')
    if (!token) return

    const response = await fetch(`${config.apiBaseUrl}/admin/leads/${lead.id}/follow-up`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        is_followed_up: !lead.is_followed_up
      })
    })

    if (response.ok) {
      lead.is_followed_up = !lead.is_followed_up
    }
  } catch (error) {
    console.error('Error updating lead:', error)
  }
}

const bulkUpdateFollowUp = async (isFollowedUp) => {
  if (selectedLeads.value.length === 0) return

  try {
    const token = localStorage.getItem('admin_token')
    if (!token) return

    const response = await fetch(`${config.apiBaseUrl}/admin/leads/bulk-follow-up`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        lead_ids: selectedLeads.value,
        is_followed_up: isFollowedUp
      })
    })

    if (response.ok) {
      // Update local state
      leads.value.forEach(lead => {
        if (selectedLeads.value.includes(lead.id)) {
          lead.is_followed_up = isFollowedUp
        }
      })
      
      selectedLeads.value = []
      selectAll.value = false
    }
  } catch (error) {
    console.error('Error bulk updating leads:', error)
  }
}

const exportLeads = async () => {
  try {
    const token = localStorage.getItem('admin_token')
    if (!token) return

    // Build query parameters for export (include all filters)
    const params = new URLSearchParams()
    if (filters.showId) {
      params.append('show_id', filters.showId)
    }
    if (filters.search) {
      params.append('search', filters.search)
    }
    if (filters.followUpStatus !== '') {
      params.append('follow_up_status', filters.followUpStatus)
    }
    if (filters.dateRange) {
      const days = parseInt(filters.dateRange)
      const date = new Date()
      date.setDate(date.getDate() - days)
      params.append('date_from', date.toISOString().split('T')[0])
    }

    const response = await fetch(`${config.apiBaseUrl}/admin/leads/export?${params}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (response.ok) {
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `leads_export_${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    }
  } catch (error) {
    console.error('Error exporting leads:', error)
  }
}

const exportSelected = async () => {
  if (selectedLeads.value.length === 0) return
  try {
    const token = localStorage.getItem('admin_token')
    if (!token) return

    const response = await fetch(`${config.apiBaseUrl}/admin/leads/export`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ lead_ids: selectedLeads.value })
    })

    if (response.ok) {
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `selected_leads_${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    }
  } catch (error) {
    console.error('Error exporting selected leads:', error)
  }
}

const viewLeadDetail = (lead) => {
  selectedLead.value = lead
  showLeadDetail.value = true
}

const goBack = () => {
  router.push('/admin/dashboard')
}

const formatDateTime = (dateString) => {
  return new Date(dateString).toLocaleString()
}

// Watch for filter changes
watch([() => filters.showId, () => filters.followUpStatus, () => filters.dateRange, () => filters.search], () => {
  pagination.page = 1
  loadLeads()
})

// Lifecycle
onMounted(() => {
  // Extract URL parameters and set initial filters
  const urlShowId = route.query.show_id
  if (urlShowId) {
    filters.showId = urlShowId
  }
  
  loadShows()
  loadLeads()
})
</script> 